import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquareText, X, Send, Check, Loader2, ArrowRight, Paperclip, ImageIcon } from 'lucide-react';
import api from '@/utils/api';
import { openWhatsApp as openWhatsAppHelper } from '@/utils/phone';
import { AIBadge, AINotice, AIFirstInteraction } from '@/components/AIDisclosure';
import { AI_DISCLOSURE } from '@/config/compliance';

const STORAGE_SID = 'bekaj_chat_session_id';
const STORAGE_LEAD = 'bekaj_chat_lead_submitted';

// Quick-Reply Liste laut User-Vorgabe
const SERVICES = ['Bodenarbeiten', 'Sanierung', 'Renovierung', 'Malerarbeiten', 'Sonstiges'];
const TIMELINES = ['Sofort', 'Innerhalb 4 Wochen', '1 - 3 Monate', 'Flexibel'];
const PHOTO_OPTIONS = ['Ja, ich habe Fotos', 'Nein, noch nicht'];
// Erst offene Beschreibung, dann gezielt nachfragen
const STEPS = ['project', 'service', 'timeline', 'photos', 'contact', 'done'];

// Natuerliche kurze Bestaetigungen (Fallback wenn kein LLM oder zwischen Schritten)
const ACKS = ['Verstanden.', 'Alles klar.', 'Notiert.', 'Danke fuer die Info.', 'Gut zu wissen.'];
const pickAck = () => ACKS[Math.floor(Math.random() * ACKS.length)];

// Folgefragen pro Schritt - menschlich, nicht "Bitte waehlen Sie ..."
const STEP_FOLLOWUP = {
  service: 'Geht es eher um Renovierung, Sanierung, Bodenarbeiten oder Malerarbeiten?',
  timeline: 'Wann soll das ungefaehr umgesetzt werden?',
  photos: 'Haben Sie Fotos oder Videos von der Baustelle? Das hilft uns bei der Einschaetzung - Sie koennen sie direkt hier hochladen.',
  contact: 'Wie koennen wir Sie am besten erreichen? Bitte senden Sie uns Ihren Namen und Ihre Telefonnummer.',
};

const ChatWidget = ({ settings }) => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionError, setSessionError] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState('project');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [lead, setLead] = useState({ name: '', phone: '', email: '', location: '', dsgvo: false });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(
    () => localStorage.getItem(STORAGE_LEAD) === '1'
  );
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const wa = settings?.whatsapp_number || '4917620283482';

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, step, uploads]);

  const initSession = useCallback(async () => {
    if (sessionId) return sessionId;
    try {
      setSessionError(false);
      const utm = new URLSearchParams(window.location.search);
      const metadata = {
        source: utm.get('utm_source') || document.referrer || 'direct',
        utm_source: utm.get('utm_source') || '',
        utm_medium: utm.get('utm_medium') || '',
        utm_campaign: utm.get('utm_campaign') || '',
        device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        page: window.location.pathname,
      };
      const res = await api.post('/chat/start', { metadata });
      const sid = res.data.session_id;
      setSessionId(sid);
      localStorage.setItem(STORAGE_SID, sid);
      // Greeting enthaelt bereits die erste offene Frage - keine zweite Bot-Nachricht
      setMessages([
        { role: 'assistant', content: res.data.greeting },
      ]);
      return sid;
    } catch (e) {
      setSessionError(true);
      const msg = e.response?.data?.detail || e.message || 'Verbindung fehlgeschlagen';
      setError(`Chat konnte nicht gestartet werden: ${msg}`);
      return null;
    }
  }, [sessionId]);

  const handleOpen = () => {
    setOpen(true);
    if (!sessionId) initSession();
  };

  const advanceStep = (current) => {
    const idx = STEPS.indexOf(current);
    if (idx < 0 || idx >= STEPS.length - 1) return current;
    return STEPS[idx + 1];
  };

  const stepPrompt = (s) => STEP_FOLLOWUP[s] || '';

  const pushAssistant = (text, delay = 0) => {
    if (delay) {
      setTimeout(() => setMessages((p) => [...p, { role: 'assistant', content: text }]), delay);
    } else {
      setMessages((p) => [...p, { role: 'assistant', content: text }]);
    }
  };

  // Core handler - text or chip
  const handleAnswer = async (text, opts = {}) => {
    if (!text || sending) return;
    let sid = sessionId;
    if (!sid) {
      sid = await initSession();
      if (!sid) {
        setError('Verbindung fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }
    }
    const currentStep = step;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    setError('');

    // Bei freien Texteingaben (project step) lassen wir die LLM antworten.
    // Bei Chip-Klicks (deterministisch) nutzen wir nur eine kurze Bestaetigung +
    // die naechste Frage, kein zusaetzlicher LLM-Reply (klingt sonst doppelt).
    const useLLM = !opts.skipLLM && currentStep === 'project';

    let llmReply = '';
    let intent = null;
    try {
      if (useLLM) {
        const res = await api.post('/chat/message', { session_id: sid, message: text });
        llmReply = (res.data.reply || '').trim();
        intent = res.data.intent || null;
      } else {
        // Hintergrund: trotzdem fuer den Kontext speichern, aber nicht warten
        api.post('/chat/message', { session_id: sid, message: text }).catch(() => {});
      }
    } catch (err) {
      llmReply = '';
    }

    // Bei Intent (z.B. address) NICHT zum naechsten Schritt springen -
    // der Nutzer soll erst seine Frage geklaert bekommen und dann ggf. weitermachen.
    const skipAdvance = intent !== null;
    const next = skipAdvance ? currentStep : advanceStep(currentStep);
    const followUp = skipAdvance ? '' : (STEP_FOLLOWUP[next] || '');

    // Eine Bot-Antwort senden: bei LLM-Step die LLM-Antwort,
    // bei Chip-Step eine kurze Bestaetigung + die naechste Frage in einer Nachricht.
    let botMessage;
    if (useLLM && llmReply) {
      botMessage = llmReply;
      // Bei reinen Intent-Antworten (z.B. Adresse) NICHTS anhaengen
      if (!skipAdvance && followUp && !/\?/.test(llmReply)) {
        botMessage = `${llmReply} ${followUp}`;
      }
    } else {
      botMessage = followUp ? `${pickAck()} ${followUp}` : pickAck();
    }
    setMessages((prev) => [...prev, { role: 'assistant', content: botMessage }]);

    if (next !== currentStep) setStep(next);
    setSending(false);
  };

  const handleTextSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    // Auf der Projekt-Stufe: echte LLM-Antwort.
    // Auf Chip-Stufen: kurze Bestaetigung + naechste Frage.
    await handleAnswer(text);
  };

  const handleChip = (label) => {
    handleAnswer(label, { skipLLM: true });
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let sid = sessionId;
    if (!sid) {
      sid = await initSession();
      if (!sid) { setError('Verbindung fehlgeschlagen.'); return; }
    }
    setUploading(true);
    setError('');
    const newUploads = [];
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) {
        setError(`${f.name} zu gross (max 15 MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post(`/chat/upload?session_id=${encodeURIComponent(sid)}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newUploads.push({ id: res.data.id, filename: res.data.filename, size: res.data.size });
      } catch (err) {
        setError(err.response?.data?.detail || `Upload von ${f.name} fehlgeschlagen`);
      }
    }
    if (newUploads.length) {
      setUploads((prev) => [...prev, ...newUploads]);
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: `${newUploads.length} Datei(en) hochgeladen: ${newUploads.map((u) => u.filename).join(', ')}` },
        { role: 'assistant', content: `Vielen Dank, ${newUploads.length === 1 ? 'die Datei wurde' : 'die Dateien wurden'} hochgeladen.` },
      ]);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.phone.trim() || !lead.dsgvo) {
      setError('Bitte Name, Telefon und Datenschutz-Zustimmung ausfuellen.');
      return;
    }
    setLeadLoading(true);
    setError('');
    try {
      await api.post('/chat/lead', {
        session_id: sessionId,
        name: lead.name.trim(),
        phone: lead.phone.trim(),
        email: lead.email.trim() || null,
        location: lead.location.trim() || null,
        dsgvo_consent: true,
      });
      localStorage.setItem(STORAGE_LEAD, '1');
      setLeadSubmitted(true);
      setStep('done');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Vielen Dank, ${lead.name.split(' ')[0]}. Wir melden uns innerhalb von 24 Stunden bei Ihnen.` },
      ]);
      if (window.dataLayer) window.dataLayer.push({ event: 'generate_lead', source: 'ai_chat' });
      if (window.fbq) window.fbq('track', 'Lead', { source: 'ai_chat' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Senden fehlgeschlagen.');
    } finally {
      setLeadLoading(false);
    }
  };

  const openWhatsApp = () => {
    const msg = 'Hallo BEKAJ BAU, ich habe gerade eine Anfrage ueber den Projekt-Assistenten gesendet.';
    openWhatsAppHelper(wa, msg);
  };

  // Map chat step -> chip set (extracted for readability vs nested ternary).
  const CHIPS_BY_STEP = {
    service: SERVICES,
    timeline: TIMELINES,
    photos: PHOTO_OPTIONS,
  };
  const currentChips = CHIPS_BY_STEP[step] || null;

  const showInput = !leadSubmitted && step !== 'contact' && step !== 'done';
  // Upload immer verfuegbar waehrend des Gespraechs - der Bot sagt explizit Bescheid
  // wann es Sinn macht. So kann der Nutzer auch frueher schon Bilder schicken.
  const showUpload = !leadSubmitted && step !== 'contact' && step !== 'done';

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={handleOpen}
          data-testid="chat-widget-toggle"
          data-track="ai_chat_open"
          aria-label="Projekt-Assistent oeffnen"
          className={`fixed z-[55] bottom-20 right-4 sm:bottom-5 sm:right-5 group bg-white text-[#3a2715] border border-[#4A331D]/12 rounded-full p-2.5 sm:pl-2.5 sm:pr-4 sm:py-2 flex items-center gap-2
                      shadow-[0_4px_18px_-4px_rgba(74,51,29,0.18)] hover:shadow-[0_8px_24px_-6px_rgba(74,51,29,0.25)]
                      transition-all duration-300 ease-out
                      ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
                      hover:-translate-y-0.5 active:scale-[0.97]`}
        >
          <span className="w-7 h-7 rounded-full bg-[#4A331D] text-[#D4AA6A] flex items-center justify-center transition-transform duration-300 group-hover:rotate-[8deg]">
            <MessageSquareText className="w-3.5 h-3.5" strokeWidth={2.2} />
          </span>
          <span className="hidden sm:inline text-[13px] font-medium tracking-tight">Projekt-Assistent</span>
        </button>
      )}

      {/* Panel: bottom-anchored only so mobile keyboard pushes it up cleanly */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed z-[60] inset-x-2 bottom-2 md:inset-auto md:bottom-6 md:right-6 md:w-[380px] bg-white rounded-2xl flex flex-col overflow-hidden border border-[#4A331D]/10 shadow-[0_20px_50px_-12px_rgba(74,51,29,0.28)] animate-[fadeInUp_0.25s_ease-out]"
          style={{ height: 'min(620px, calc(100dvh - 16px))', maxHeight: 'calc(100dvh - 16px)' }}
        >
          {/* Header */}
          <div className="bg-[#4A331D] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-[#D4AA6A]/15 border border-[#D4AA6A]/40 flex items-center justify-center">
                <MessageSquareText className="w-4 h-4 text-[#D4AA6A]" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[13px] font-semibold tracking-tight flex items-center gap-1.5" data-testid="chat-widget-title">
                  {AI_DISCLOSURE.chatbotName}
                  <AIBadge variant="dark" />
                </div>
                <div className="text-[10.5px] text-white/55 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  BEKAJ BAU
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-widget-close"
              aria-label="Schliessen"
              className="text-white/60 hover:text-white p-1 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* EU AI Act Art. 50 – KI-Transparenzhinweis */}
          <AINotice />


          {/* Progress */}
          {!leadSubmitted && (
            <div className="h-0.5 bg-[#FAF7F2] flex-shrink-0">
              <div className="h-full bg-[#D4AA6A] transition-all duration-500" style={{ width: `${(STEPS.indexOf(step) / (STEPS.length - 1)) * 100}%` }} />
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-[#FBF9F5] min-h-0" data-testid="chat-messages">
            {/* Erst-Interaktions-Hinweis nach Art. 50 EU AI Act */}
            {messages.length > 0 && <AIFirstInteraction />}

            {sessionError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-700">
                Chat konnte nicht gestartet werden.{' '}
                <button onClick={initSession} className="underline font-medium">Erneut versuchen</button>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-[fadeIn_0.25s_ease-out]`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-line ${
                  m.role === 'user' ? 'bg-[#4A331D] text-white rounded-br-sm' : 'bg-white text-[#2a2018] rounded-bl-sm border border-[#4A331D]/8'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}

            {uploads.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="chat-uploads">
                {uploads.map((u) => (
                  <div key={u.id} className="text-[11px] px-2.5 py-1 rounded-full bg-[#D4AA6A]/15 border border-[#D4AA6A]/40 text-[#3a2715] flex items-center gap-1.5">
                    <ImageIcon className="w-3 h-3" />
                    <span className="max-w-[120px] truncate">{u.filename}</span>
                  </div>
                ))}
              </div>
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#4A331D]/8 rounded-2xl rounded-bl-sm px-3 py-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-[#4A331D]/30 rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-[#4A331D]/30 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                    <span className="w-1.5 h-1.5 bg-[#4A331D]/30 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Contact form */}
            {step === 'contact' && !leadSubmitted && (
              <form onSubmit={handleLeadSubmit} className="bg-white rounded-2xl p-3 border border-[#4A331D]/10 space-y-2 mt-1" data-testid="chat-lead-form">
                <Input testid="chat-lead-name" placeholder="Ihr Name *" value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} required />
                <Input testid="chat-lead-phone" placeholder="Telefonnummer *" type="tel" value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} required />
                <Input testid="chat-lead-email" placeholder="E-Mail (optional)" type="email" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} />
                <Input testid="chat-lead-location" placeholder="Ort / PLZ (optional)" value={lead.location} onChange={(v) => setLead({ ...lead, location: v })} />
                <label className="flex gap-2 text-[10.5px] text-gray-600 leading-snug pt-1">
                  <input
                    type="checkbox"
                    checked={lead.dsgvo}
                    onChange={(e) => setLead({ ...lead, dsgvo: e.target.checked })}
                    className="mt-0.5 flex-shrink-0 accent-[#4A331D]"
                    data-testid="chat-lead-dsgvo"
                  />
                  <span>
                    Ich stimme der Verarbeitung meiner Angaben zur Bearbeitung der Anfrage zu.{' '}
                    <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#4A331D] underline">Datenschutz</a>
                  </span>
                </label>
                {error && <div className="text-[11px] text-red-600">{error}</div>}
                <button
                  type="submit"
                  disabled={leadLoading || !lead.name.trim() || !lead.phone.trim() || !lead.dsgvo}
                  data-testid="chat-lead-submit"
                  data-track="ai_chat_submit"
                  className="w-full bg-[#4A331D] hover:bg-[#3a2715] disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium text-[13px] py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:gap-3"
                >
                  {leadLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Anfrage senden
                </button>
              </form>
            )}

            {/* Done */}
            {leadSubmitted && step === 'done' && (
              <div className="bg-white rounded-2xl border border-[#4A331D]/10 p-3 mt-2 space-y-2.5" data-testid="chat-done-block">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-[#4A331D]">
                  <span className="w-6 h-6 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  Anfrage erfolgreich gesendet
                </div>
                <p className="text-[12px] text-gray-600 leading-snug">
                  Bei dringenden Fragen koennen Sie uns optional per WhatsApp erreichen.
                </p>
                <button
                  onClick={openWhatsApp}
                  data-testid="chat-done-whatsapp"
                  className="w-full text-[12px] py-2 rounded-lg border border-[#4A331D]/15 text-[#4A331D] hover:bg-[#FAF7F2] transition-colors flex items-center justify-center gap-1.5"
                >
                  Optional via WhatsApp anschreiben
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Chips bar */}
          {showInput && currentChips && (
            <div className="px-3 py-2 border-t border-[#4A331D]/8 bg-white flex flex-wrap gap-1.5 flex-shrink-0" data-testid={`chat-chips-${step}`}>
              {currentChips.map((c) => (
                <Chip key={c} label={c} onClick={() => handleChip(c)} disabled={sending} />
              ))}
            </div>
          )}

          {/* Eingabezeile - immer sichtbar bei aktivem Chat */}
          {showInput && (
            <form onSubmit={handleTextSend} className="border-t border-[#4A331D]/10 p-2.5 bg-white flex gap-2 items-center flex-shrink-0">
              {showUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/mp4,video/quicktime,application/pdf"
                    multiple
                    onChange={handleFiles}
                    className="hidden"
                    data-testid="chat-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || sending}
                    aria-label="Datei hochladen"
                    data-testid="chat-upload-btn"
                    className="flex-shrink-0 w-10 h-10 rounded-lg border border-[#4A331D]/15 text-[#4A331D] hover:bg-[#FAF7F2] disabled:opacity-40 flex items-center justify-center transition-colors"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  </button>
                </>
              )}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ihre Nachricht eingeben…"
                disabled={sending}
                inputMode="text"
                enterKeyHint="send"
                autoComplete="off"
                className="flex-1 min-w-0 px-3 py-2.5 text-[15px] md:text-[14px] bg-white border border-[#4A331D]/20 rounded-lg focus:outline-none focus:border-[#D4AA6A] focus:ring-2 focus:ring-[#D4AA6A]/20 disabled:opacity-60 transition-all"
                data-testid="chat-input"
                style={{ fontSize: '16px' }} /* iOS-Safari: 16px verhindert Auto-Zoom beim Fokus */
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="chat-send"
                aria-label="Senden"
                className="flex-shrink-0 bg-[#4A331D] hover:bg-[#3a2715] disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium text-[13px] px-3.5 h-10 rounded-lg transition-transform duration-200 active:scale-95 flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Senden</span>
              </button>
            </form>
          )}

          {error && showInput && (
            <div className="px-3 pb-2 text-[11px] text-red-600 bg-white flex-shrink-0" data-testid="chat-error">{error}</div>
          )}
        </div>
      )}

      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform: translateY(8px);} to { opacity:1; transform: translateY(0);} }
        @keyframes fadeIn { from { opacity:0;} to { opacity:1;} }
      `}</style>
    </>
  );
};

const Chip = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="text-[12px] px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#4A331D]/15 text-[#3a2715] hover:bg-[#4A331D] hover:text-white hover:border-[#4A331D] disabled:opacity-40 disabled:hover:bg-[#FAF7F2] disabled:hover:text-[#3a2715] transition-all duration-200 active:scale-95"
    data-testid={`chat-chip-${label.replace(/\s+/g, '-').toLowerCase()}`}
  >
    {label}
  </button>
);

const Input = ({ testid, placeholder, type = 'text', value, onChange, required }) => (
  <input
    type={type}
    placeholder={placeholder}
    required={required}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 text-[13px] bg-[#FAF7F2] border border-transparent rounded-lg focus:outline-none focus:border-[#D4AA6A] focus:bg-white transition-colors"
    style={{ fontSize: '16px' }}
    data-testid={testid}
  />
);

export default ChatWidget;
